import React from 'react';
import type { PageKey, Workspace } from '../types';
import { MenuIcon } from './NavIcons';
import { Logo } from './ConistraMark';
import './TopBar.css';

const pageTitles: Record<PageKey, string> = {
  dashboard: 'Dashboard',
  tasks: 'Needs You',
  assistant: 'The Oracle',
  strategy: 'Strategy',
  pillars: 'Content Pillars',
  plans: 'Content Plans',
  content: 'Outlines & Posts',
  calendar: 'Content Calendar',
  schedule: 'Schedule',
  media: 'Media',
  supervisor: 'Supervisor Agent',
  engagement: 'Engagement Agent',
  settings: 'Settings',
  activate: 'Activation',
};

type TopBarProps = {
  currentPage: PageKey;
  onMenuToggle: () => void;
  workspace?: Workspace | null;
  onNavigate?: (_page: PageKey) => void;
};

/** True when the supervisor (autopilot) agent is actively running the account. */
function isAutopilotOn(ws?: Workspace | null): boolean {
  return Boolean(ws?.supervisor_enabled);
}

function accountHandle(ws?: Workspace | null): string | null {
  const handle = ws?.instagrapi_username || ws?.instagram_handle;
  if (!handle) return null;
  return handle.startsWith('@') ? handle : `@${handle}`;
}

function TopBar({ currentPage, onMenuToggle, workspace, onNavigate }: TopBarProps) {
  const handle = accountHandle(workspace);
  const connected = Boolean(workspace?.has_instagrapi_session || workspace?.has_instagram_token);
  const needsAttention = Boolean(workspace?.instagram_needs_attention);
  const autopilot = isAutopilotOn(workspace);

  // The connection/autopilot status shown in the top bar.
  let statusLabel: string;
  let statusTone: 'ok' | 'warn' | 'idle';
  if (needsAttention) {
    statusLabel = 'Needs you';
    statusTone = 'warn';
  } else if (!connected) {
    statusLabel = 'Not connected';
    statusTone = 'idle';
  } else if (autopilot) {
    statusLabel = 'Autopilot on';
    statusTone = 'ok';
  } else {
    statusLabel = 'Manual mode';
    statusTone = 'idle';
  }

  const avatarInitial = (handle ?? workspace?.name ?? 'C').replace('@', '').charAt(0).toUpperCase();

  return (
    <header className="topbar" role="banner">
      {/* Mobile-only hamburger + wordmark */}
      <button
        className="topbar-menu-btn"
        onClick={onMenuToggle}
        aria-label="Open navigation menu"
        aria-haspopup="true"
      >
        <MenuIcon size={20} />
      </button>
      <div className="topbar-brand" aria-label="Conistra">
        <Logo size={22} />
      </div>

      {/* Desktop account identity */}
      <div className="topbar-account">
        {workspace?.profile_image_url ? (
          <img className="topbar-avatar" src={workspace.profile_image_url} alt="" aria-hidden="true" />
        ) : (
          <span className="topbar-avatar topbar-avatar--fallback" aria-hidden="true">
            {avatarInitial}
          </span>
        )}
        <div className="topbar-account-meta">
          <div className="topbar-account-handle">{handle ?? workspace?.name ?? 'Your account'}</div>
          <div className={`topbar-account-sub topbar-account-sub--${statusTone}`}>
            <span className="topbar-status-dot" aria-hidden="true" />
            {statusLabel}
          </div>
        </div>
      </div>

      {/* Mobile page title (hidden on desktop where account identity shows) */}
      <h1 className="topbar-page-title">{pageTitles[currentPage]}</h1>

      {/* Desktop actions */}
      <div className="topbar-actions">
        <span className={`topbar-pill topbar-pill--${statusTone}`}>
          <span className="topbar-status-dot" aria-hidden="true" />
          {statusLabel}
        </span>
        {onNavigate && currentPage !== 'activate' && (
          <button
            className="topbar-cta"
            onClick={() => onNavigate('assistant')}
            type="button"
          >
            Ask the Oracle
          </button>
        )}
      </div>
    </header>
  );
}

export default TopBar;
